import { Avatar, Box, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  usePerformanceInsightsSummaryDatatable,
} from 'generated/resources/datatable.graphql'
import {
  AnalyticsPageGendersFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
  TeamsTeam,
} from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  EmployeePulseIcon,
  getEmployeeMoodToolTip,
} from 'modules/engagement/components/EmployeePulseIcon'
import Actions from 'modules/performance_analytics/components/Actions'
import { AggregationsSidepanel } from 'modules/performance_analytics/components/AggregationsSidepanel'
import BlankSlate from 'modules/performance_analytics/components/BlankSlate'
import ScoreTag from 'modules/performance_analytics/components/ScoreTag'
import WithToolTip from 'modules/performance_analytics/components/WithTooltip'
import { ChartType } from 'modules/performance_analytics/lib/ChartContext'

import { EngagementSignalsCell } from './EngagementSignalsCell'
import employeeFilter from './filters/employeeFilter'
import gendersFilter from './filters/gendersFilter'
import gridFilter from './filters/gridFilter'
import legalEntitiesFilter from './filters/legalEntitiesFilter'
import managerFilter from './filters/managerFilter'
import nationalitiesFilter from './filters/nationalitiesFilter'
import potentialScoreFilter from './filters/potentialScoreFilter'
import projectsFilter from './filters/projectsFilter'
import pulseAnswerFilter from './filters/pulseAnswerFilter'
import scoreFilter from './filters/scoreFilter'
import teamsFilter from './filters/teamsFilter'
import tenureDateFilter from './filters/tenureDateFilter'
import workspacesFilter from './filters/workspacesFilter'

type DashboardFilter = ChartType | 'headcount'
interface Props {
  children: React.ReactNode
  teams: Pick<TeamsTeam, 'id' | 'name'>[]
  hasReviewProcesses: boolean
  canCreateReviewProcesses: boolean
  engagementSignalsEnabled: boolean
  aggregationsEnabled: boolean
  employeePulseQuestionEnabled: boolean
  developmentMode: boolean
  genders: AnalyticsPageGendersFragment[]
  locations: AnalyticsPageLocationsFragment[]
  legalEntities: AnalyticsPageLegalEntitiesFragment[]
  nationalities: string[]
  activeFilter: DashboardFilter
  performanceEnabled: boolean
  projectsEnabled: boolean
}

const calculateCoordinate = (score: number | null | undefined): number | undefined =>
  score === undefined || score === null ? undefined : score <= 32 ? 0 : score <= 65 ? 1 : 2

const calculateSingleCoordinate = (
  x: number | undefined,
  y: number | undefined
): number | undefined => (x === undefined || y === undefined ? undefined : y * 3 + x + 1)

const PerformanceInsightsSummaryDatatable = ({
  children,
  teams,
  hasReviewProcesses,
  canCreateReviewProcesses,
  engagementSignalsEnabled,
  aggregationsEnabled,
  employeePulseQuestionEnabled,
  developmentMode,
  genders,
  locations,
  legalEntities,
  nationalities,
  activeFilter,
  performanceEnabled,
  projectsEnabled,
}: Props) => {
  const source = usePerformanceInsightsSummaryDatatable(
    [
      {
        identifier: 'employee',
        title: i18n.t('performance_process.analytics.datatable.header.employee'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ targetAccess_fullName }}',
        sortBy: 'employeeFullName',
        render: (review) => {
          const access = review.targetAccess

          return (
            access && (
              <Box flexDirection="row" alignItems="center" gap="s16" width="s200">
                <Avatar size="s40" url={buildUrl(access.avatar)} label={access.fullName} />
                <Text.Normal size="200" weight="medium">
                  {access.fullName}
                </Text.Normal>
              </Box>
            )
          )
        },
      },
      {
        identifier: 'team',
        title: i18n.t('performance_process.analytics.datatable.header.team'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: `{% if targetAccess_employee_memberships %} {% for membership in targetAccess_employee_memberships %} {{ membership.team.name }} {% endfor %} {% endif %}
      `,
        render: (review) => {
          const memberships = review?.targetAccess?.employee?.memberships || []

          return (
            <Box overflowY="auto" hideScrollBar justifyContent="center" width="s120">
              {memberships.map(({ team }) => team?.name).join(', ')}
            </Box>
          )
        },
      },
      {
        identifier: 'manager',
        title: i18n.t('performance_process.analytics.datatable.header.manager'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ targetAccess_employee_manager_access_fullName }}',
        sortBy: 'managerFullName',
        render: (review) => {
          const manager = review?.targetAccess?.employee?.manager
          const access = manager?.access

          return (
            access && (
              <Box>
                {access && (
                  <WithToolTip label={access.fullName}>
                    <Box flexDirection="row" alignItems="center" gap="s16" minWidth="s96">
                      <Avatar size="s40" url={buildUrl(access.avatar)} label={access.fullName} />
                      <Text.Normal size="200" weight="medium" whiteSpace="nowrap">
                        {access.fullName}
                      </Text.Normal>
                    </Box>
                  </WithToolTip>
                )}
              </Box>
            )
          )
        },
      },
      {
        identifier: 'pulseSurvey',
        title: i18n.t('performance_process.analytics.datatable.header.employee_pulse'),
        tooltip: i18n.t('performance_process.analytics.datatable.header.employee_pulse_hint'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: !employeePulseQuestionEnabled || activeFilter === '9_box_grid',
        template: '{{ pulseAnswer }}',
        sortBy: 'pulseAnswer',
        render: ({ employeePulse }) => {
          const answer = employeePulse?.value ?? undefined

          return (
            <Box width="s96" alignItems="center">
              <WithToolTip
                label={answer ? getEmployeeMoodToolTip(answer) : ''}
                trackingGroup="pulse-survey"
              >
                <EmployeePulseIcon mood={answer} />
              </WithToolTip>
            </Box>
          )
        },
      },
      {
        identifier: 'pulse_information',
        title: i18n.t('performance_process.analytics.datatable.header.last_pulses'),
        hidden: !employeePulseQuestionEnabled || activeFilter === '9_box_grid',
        kind: ProjectionKind.Custom,
        template:
          '{% for edge in pulsesConnection_edges %} Value: {{ edge.node.value }} | Comment: {% if edge.node.comment %}{{ edge.node.comment }}{% else %}""{% endif %} | Submitted date: {{ edge.node.submittedDate }} \n {% endfor %}',
        render: (review) => {
          const days = Array.from({ length: 7 }, (_, i) =>
            moment()
              .subtract(6 - i, 'days')
              .format('YYYY-MM-DD')
          )

          const pulseDataByDate: Record<string, (typeof review.pulsesConnection.edges)[0]['node']> =
            {}
          review.pulsesConnection.edges.forEach((edge) => {
            const pulseDate = moment(edge.node.submittedDate).format('YYYY-MM-DD')
            pulseDataByDate[pulseDate] = edge.node
          })

          return (
            <Box flexDirection="row">
              {days.map((date, index) => {
                const pulse = pulseDataByDate[date]
                const key = pulse?.id || `empty-${date}`

                return (
                  <Box key={key} position="relative" alignItems="center" justifyContent="center">
                    <Box
                      alignItems="center"
                      justifyContent="center"
                      position="absolute"
                      style={{ left: `${index * 15}px`, zIndex: '0' }}
                    >
                      <Box style={{ zIndex: '0' }}>
                        <WithToolTip label={pulse?.comment}>
                          <EmployeePulseIcon
                            hasComment={pulse ? !!pulse.comment : false}
                            mood={pulse?.value || undefined}
                          />
                        </WithToolTip>
                      </Box>

                      <Box
                        position="absolute"
                        background="white"
                        borderRadius={{ all: 'rel1000' }}
                        style={{ width: '20px', height: '20px', zIndex: '-10' }}
                      />
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )
        },
      },
      {
        identifier: 'score',
        title: i18n.t('performance_process.analytics.datatable.header.last_performance_score'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: `{{ reviewEmployeeScore_score }}/{{ reviewEmployeeScore_scaleMax }}`,
        sortBy: 'normalizedScore',
        hidden: !performanceEnabled,
        render: (review) => {
          review.reviewEmployeeScore_scaleMax
          const score = review.reviewEmployeeScore?.score
          const scaleMax = review.reviewEmployeeScore?.scaleMax
          // in the case the employee doesn't have a score and maybe also doesn't have a review process
          // we should display a blank state
          if (!score || !scaleMax) {
            return <Box minWidth="s140" />
          }
          const reviewProcess = review.reviewProcess
          if (!reviewProcess) {
            return <Box minWidth="s140" />
          }
          const { name, startsAt, endsAt } = reviewProcess
          const start = startsAt && dateToString(moment(startsAt), 'ui_date_day_month_year')
          const end = endsAt && dateToString(moment(endsAt), 'ui_date_day_month_year')

          return (
            <Box minWidth="s140">
              <WithToolTip label={`${name} (${start} - ${end})`} trackingGroup="performance-score">
                <ScoreTag score={score} maxScore={scaleMax} />
              </WithToolTip>
            </Box>
          )
        },
      },
      {
        identifier: 'potentialScore',
        title: i18n.t('performance_process.analytics.datatable.header.last_potential_score'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: !(activeFilter === '9_box_grid') || !performanceEnabled,
        template: `{{ potentialScore }}/{{ reviewEmployeeScore_scaleMax }}`,
        sortBy: 'normalizedPotentialScore',
        render: (review) => {
          const score = review.potentialScore
          const scaleMax = review.reviewEmployeeScore?.scaleMax
          if (!score || !scaleMax) {
            return <Box minWidth="s140" />
          }
          const reviewProcess = review.reviewProcess
          if (!reviewProcess) {
            return <Box minWidth="s140" />
          }
          const { name, startsAt, endsAt } = reviewProcess
          const start = startsAt && dateToString(moment(startsAt), 'ui_date_day_month_year')
          const end = endsAt && dateToString(moment(endsAt), 'ui_date_day_month_year')

          return (
            <Box minWidth="s120">
              <WithToolTip label={`${name} (${start} - ${end})`} trackingGroup="potential-score">
                <ScoreTag score={score} maxScore={scaleMax} />
              </WithToolTip>
            </Box>
          )
        },
      },
      {
        identifier: 'status',
        title: i18n.t('performance_process.analytics.datatable.header.nine_box'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: !(activeFilter === '9_box_grid') || !performanceEnabled,
        template: `{% if reviewEmployeeScore_normalizedScore and normalizedPotentialScore %}{% assign normalizedScore = reviewEmployeeScore_normalizedScore | floor %}{% assign potentialNormalizedScore = normalizedPotentialScore | floor %}{{ normalizedScore | calculate_nine_box_score: potentialNormalizedScore }}{% else %} - {% endif %}`,
        sortBy: 'gridStatus',
        render: (review) => {
          const normalizedScore = review.reviewEmployeeScore?.normalizedScore
          const normalizedPotentialScore = review.normalizedPotentialScore

          const xCoord = calculateCoordinate(normalizedScore)
          const yCoord = calculateCoordinate(normalizedPotentialScore)
          const singleCoordinate = calculateSingleCoordinate(xCoord, yCoord)
          const coordinateText = (() => {
            switch (singleCoordinate) {
              case 1:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.1')
              case 2:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.2')
              case 3:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.3')
              case 4:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.4')
              case 5:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.5')
              case 6:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.6')
              case 7:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.7')
              case 8:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.8')
              case 9:
                return i18n.t('performance_process.analytics.nine_box_grid.scores.9')
              default:
                return '-'
            }
          })()
          return (
            <Box minWidth="s120">
              <Text.Normal size="200" weight="semibold" whiteSpace="nowrap">
                {coordinateText}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'engagement_signals',
        title: i18n.t('performance_process.analytics.datatable.header.engagement_signals'),
        fixed: true,
        hidden:
          !engagementSignalsEnabled ||
          activeFilter === '9_box_grid' ||
          activeFilter === 'engagement_signals',
        kind: ProjectionKind.Custom,
        width: 300,
        template: '{{ insights_engagementSignals }}',
        tooltip: i18n.t('performance_process.analytics.datatable.header.engagement_signals_hint'),
        render: (review) => {
          return (
            <EngagementSignalsCell engagementSignals={review.insights_engagementSignals || []} />
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 150,
        fixed: true,
        sticky: true,
        render: (review) => {
          const employeeId = review?.targetAccess_employee_id

          if (!employeeId) {
            return null
          }

          return (
            <Actions
              employeeId={employeeId}
              developmentMode={developmentMode}
              onClick={() => {
                track(trackingEvents.performanceAnalytics.insightsSummaryEmployeeRowClicked, {
                  performance_score: review.reviewEmployeeScore?.score,
                })
              }}
            />
          )
        },
      },
    ],
    {
      variables: {
        pulseFrom: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        pulseTo: moment().format('YYYY-MM-DD'),
      },
    }
  )

  return (
    <DataTable.Provider
      sort={{ path: 'employeeFullName', direction: 'asc' }}
      onSortCallback={(sort) => {
        track(trackingEvents.performanceAnalytics.insightsSummaryColumnSorted, {
          column: sort.path,
          direction: sort.direction,
        })
      }}
      filters={compact([
        employeeFilter(source),
        managerFilter(source),
        teamsFilter(source, teams),
        performanceEnabled ? scoreFilter(source) : undefined,
        performanceEnabled ? potentialScoreFilter(source) : undefined,
        performanceEnabled ? gridFilter(source) : undefined,
        tenureDateFilter(source),
        employeePulseQuestionEnabled ? pulseAnswerFilter(source) : undefined,
        gendersFilter(source, genders),
        workspacesFilter(source, locations),
        projectsEnabled ? projectsFilter(source) : undefined,
        legalEntitiesFilter(source, legalEntities),
        nationalitiesFilter(source, nationalities),
      ])}
      source={source}
    >
      <Box gap="s12">
        <DataTable.HeadBar
          exportFileName={i18n.t('performance_process.analytics.datatable.export_name')}
          disableEditColumns
          inlineDefaultActions
          actions={
            <Box flexDirection="row" alignItems="center" gap="s8">
              {aggregationsEnabled && <AggregationsSidepanel />}
            </Box>
          }
          onExportCallback={() => {
            track(trackingEvents.performanceAnalytics.insightsSummaryExportClicked)
          }}
        />

        {children}
        <DataTable.Table
          autoWidth
          emptyState={
            <BlankSlate
              hasReviewProcesses={hasReviewProcesses}
              canCreateReviewProcesses={canCreateReviewProcesses}
            />
          }
        />
      </Box>
    </DataTable.Provider>
  )
}

export default PerformanceInsightsSummaryDatatable
